import React, { useRef, useState } from 'react';
import { HiOutlineCloudArrowUp, HiOutlineDocument, HiCheckCircle } from 'react-icons/hi2';
import { fileProblem, uploadKnowledgeFile } from '../api/knowledge-upload';
import { chatCard, chatIconTile, chatPrimary } from './chat-theme';
import { Spinner } from './activity';

/**
 * The copilot asked for a document (request_file_upload). The builder picks the file here;
 * it is uploaded and registered like Knowledge → Files, then the copilot is told so it can
 * wait for ingestion and wire the source up.
 */
export default function UploadCard({ request, appId, onUploaded, disabled }) {
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [state, setState] = useState({ status: 'idle', error: null });
    const accept = request.accept.map(ext => `.${ext}`).join(',');
    const done = state.status === 'done';
    const busy = state.status === 'uploading';

    const choose = event => {
        const picked = event.target.files?.[0] || null;
        setFile(picked);
        setState({ status: 'idle', error: picked ? fileProblem(picked, request.accept) : null });
    };

    const upload = async () => {
        if (!appId) {
            setState({ status: 'failed', error: 'Open the application first.' });
            return;
        }
        setState({ status: 'uploading', error: null });
        try {
            await uploadKnowledgeFile(appId, request, file);
            setState({ status: 'done', error: null });
            if (typeof onUploaded === 'function') onUploaded({ dataSourceId: request.dataSourceId, fileName: file.name });
        } catch (error) {
            setState({ status: 'failed', error: (error && error.message) || 'The upload failed. Try again.' });
        }
    };

    return (
        <div className={`${chatCard(done ? 'success' : state.status === 'failed' ? 'danger' : 'knowledge')} text-[0.85rem]`} aria-label="Upload a document">
            <div className="flex items-start gap-2.5">
                <span className={chatIconTile(done ? 'success' : state.status === 'failed' ? 'danger' : 'knowledge')}><HiOutlineCloudArrowUp className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900">Upload a document for <code className="rounded-md bg-white px-1.5 py-0.5 font-mono text-xs ring-1 ring-slate-200">{request.dataSourceId}</code></p>
                    {request.description && <p className="mt-0.5 text-gray-600">{request.description}</p>}
                    <p className="mt-0.5 text-xs text-gray-500">
                        {request.accept.map(ext => ext.toUpperCase()).join(', ')}{request.ocr ? ' · text is read with OCR' : ''}
                    </p>
                </div>
            </div>

            {done ? (
                <p className="mt-3 flex items-center gap-1.5 font-medium text-lime-700 dark:text-brand-lime">
                    <HiCheckCircle className="h-4 w-4" /> Uploaded {file?.name}
                </p>
            ) : (
                <div className="mt-3 flex items-center gap-2">
                    <input ref={inputRef} type="file" accept={accept} onChange={choose} className="hidden" aria-label="Choose a file" />
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={busy || disabled}
                        className="flex min-w-0 flex-1 items-center gap-1.5 rounded-xl border border-dashed border-slate-300 bg-white/70 px-3 py-2 text-left text-gray-700 transition-colors hover:border-slate-400 hover:bg-white disabled:opacity-50"
                    >
                        <HiOutlineDocument className="h-4 w-4 shrink-0" />
                        <span className="truncate">{file ? file.name : 'Choose a file'}</span>
                    </button>
                    <button
                        type="button"
                        onClick={upload}
                        disabled={!file || busy || disabled || Boolean(state.error && state.status === 'idle')}
                        className={chatPrimary}
                    >
                        {busy && <Spinner className="h-3.5 w-3.5" />}
                        {busy ? 'Uploading…' : 'Upload'}
                    </button>
                </div>
            )}
            {state.error && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400" role="alert">{state.error}</p>}
        </div>
    );
}
